/**
 * Shared formatting utilities used across agent detail pages.
 * Consolidates duplicate implementations of formatFileSize, timeAgo,
 * formatDateTime, and formatDuration.
 */

import { t } from '@lingui/core/macro';
import { formatDate, formatNumber } from '../i18n/i18n';

/** Format byte count as human-readable size (B / KB / MB / GB). */
export function formatFileSize(bytes?: number): string {
  if (bytes == null || bytes === 0) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * A byte count in the largest whole unit (B ... TB), at most two decimals, in
 * the active UI locale: "1,25 MB" in Hungarian, "1.25 MB" in English. `0`
 * reads "0 B".
 */
export function formatByteSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${formatNumber(bytes / Math.pow(k, i), { maximumFractionDigits: 2 })} ${sizes[i]}`;
}

/** Format a date string as relative time ("3 minutes ago", "2 days ago"). */
export function timeAgo(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  if (Number.isNaN(then)) return "-";
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return t`just now`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return t`${diffMin} minute(s) ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return t`${diffHr} hour(s) ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return t`${diffDay} day(s) ago`;
  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) return t`${diffMonth} month(s) ago`;
  const diffYear = Math.floor(diffMonth / 12);
  return t`${diffYear} year(s) ago`;
}

/**
 * Format a date for DISPLAY in the active UI locale, with the caller's fields:
 * `{ month: "short", day: "numeric" }` gives "Sep 29" in en and "szept. 29." in
 * hu. Never use it for a machine format (an export, a filename, a sort key, a
 * value sent to the server). An unparseable value gives "-" instead of the
 * English "Invalid Date" that `toLocaleString` returned.
 */
export function formatLocaleDate(
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions,
): string {
  return formatDate(value, options);
}

/** Format a number for DISPLAY in the active UI locale: "2,400" in en, "12 345" in hu. */
export function formatLocaleNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return formatNumber(value, options);
}

/**
 * The field sets of the argument-less `toLocaleDateString()`,
 * `toLocaleTimeString()` and `toLocaleString()` (ECMA-402 defaults), for the
 * sites that used them: the same fields, in the UI locale instead of the
 * browser's.
 */
export const LOCALE_DATE: Intl.DateTimeFormatOptions = { year: "numeric", month: "numeric", day: "numeric" };
export const LOCALE_TIME: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "numeric", second: "numeric" };
export const LOCALE_DATE_TIME: Intl.DateTimeFormatOptions = { ...LOCALE_DATE, ...LOCALE_TIME };

/** Format a date using the active Lingui locale (e.g. "2026. márc. 23. 14:45"). */
export function formatDateTime(dateString: string): string {
  return formatDate(dateString, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Format seconds as compact duration ("3s", "2m 15s", "1h 30m"). */
export function formatDuration(seconds?: number): string {
  if (seconds == null || seconds === 0) return "0s";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}
